import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { Leaf, Beef, Milk, Egg, TrendingUp } from 'lucide-react';

/**
 * MarketCardModal — deep-dive detail view for the 4 top summary cards on
 * Market Insights (Garden Value, Livestock Value, Dairy Monthly, Eggs
 * Monthly). Progressive disclosure: the card grid stays clean and readable,
 * this modal shows the methodology.
 *
 * Rendered as an overlay on the Market page. Dismiss via X, backdrop tap,
 * or Escape (all handled by shadcn Dialog primitive).
 */
export default function MarketCardModal({ open, onOpenChange, cardType, data }) {
  if (!cardType) return null;

  const cfg = {
    garden:    { title: 'Garden Value',    icon: Leaf, color: 'text-green-600',  bg: 'bg-green-50',  border: 'border-green-200' },
    livestock: { title: 'Livestock Value', icon: Beef, color: 'text-amber-600',  bg: 'bg-amber-50',  border: 'border-amber-200' },
    dairy:     { title: 'Dairy (Monthly)', icon: Milk, color: 'text-blue-600',   bg: 'bg-blue-50',   border: 'border-blue-200' },
    eggs:      { title: 'Eggs (Monthly)',  icon: Egg,  color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200' },
  }[cardType];

  if (!cfg) return null;
  const Icon = cfg.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`market-modal-${cardType}`}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${cfg.bg} ${cfg.border} border flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-5 h-5 ${cfg.color}`} />
            </div>
            <DialogTitle className="font-heading text-xl">{cfg.title}</DialogTitle>
          </div>
          <DialogDescription className="pt-2 text-sm">
            {cardType === 'garden' && 'Garden revenue — both projected (based on crops planted in your layouts) and actual (based on your logged harvests this calendar year).'}
            {cardType === 'livestock' && 'Estimated live-market value of your active herds and flocks, based on average weight and USDA regional averages.'}
            {cardType === 'dairy' && 'Monthly dairy revenue — both projected (based on herd size and typical yield) and actual (based on your logged milk production this month).'}
            {cardType === 'eggs' && 'Monthly egg revenue — both projected (based on flock size and typical lay rate) and actual (based on your logged egg records this month).'}
          </DialogDescription>
        </DialogHeader>

        {cardType === 'garden'    && <GardenDetail data={data} />}
        {cardType === 'livestock' && <LivestockDetail data={data} />}
        {cardType === 'dairy'     && <DairyDetail data={data} />}
        {cardType === 'eggs'      && <EggsDetail data={data} />}
      </DialogContent>
    </Dialog>
  );
}

// ---------- helpers ----------
const usd = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const int = (n) => Number(n || 0).toLocaleString('en-US');
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};
const comparisonPct = (actual, projected) => {
  if (!projected || projected <= 0) return null;
  return Math.round((actual / projected) * 100);
};

// ---------- Garden ----------
function GardenDetail({ data }) {
  const items = data?.items || [];
  const projected = data?.total || 0;
  const actual = data?.actual_total || 0;
  const hasActual = !!data?.has_actual;
  const pct = comparisonPct(actual, projected);
  return (
    <div className="space-y-4 pt-2">
      {/* Projected vs Actual split — mirrors DairyDetail / EggsDetail. */}
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg border p-3 bg-muted/40" data-testid="modal-garden-projected">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Projected (this season)</div>
          <div className="font-mono text-xl font-bold text-green-800">${usd(projected)}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{items.length} crops in layouts</div>
        </div>
        <div className={`rounded-lg border p-3 ${hasActual ? 'bg-emerald-50 border-emerald-200' : 'bg-muted/40'}`} data-testid="modal-garden-actual">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Actual (year-to-date)</div>
          <div className={`font-mono text-xl font-bold ${hasActual ? 'text-emerald-800' : 'text-muted-foreground'}`}>${usd(actual)}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {hasActual ? (
              <>
                {data.actual_lbs > 0 && <>{int(data.actual_lbs)} lbs · </>}
                {data.actual_count > 0 && <>{int(data.actual_count)} count · </>}
                {int(data.actual_events)} logged events
              </>
            ) : (
              <>No harvests logged yet — log in Garden → Harvest.</>
            )}
          </div>
          {hasActual && pct != null && (
            <div className="mt-1 text-[11px] text-emerald-700 font-medium">
              {pct}% of projected
            </div>
          )}
        </div>
      </div>

      {/* Formula caption */}
      <div className="rounded-md bg-muted/40 p-2.5 text-[11px] text-muted-foreground leading-snug">
        <b>Projected</b>: Σ (plants × avg yield/plant × price/lb) across all garden layouts.<br />
        <b>Actual</b>: Σ (logged harvest amounts, normalised to lbs or per-unit price) for the current calendar year.
      </div>

      {items.length === 0 && !hasActual ? (
        <div className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground" data-testid="modal-garden-empty">
          No crops planted or harvests logged yet. Add crops in Garden → Manage or log a harvest in Garden → Harvest to see values here.
        </div>
      ) : items.length > 0 ? (
        <div>
          <SectionLabel>Crop-by-crop projection</SectionLabel>
          <ul className="divide-y divide-muted rounded-lg border overflow-hidden">
            {items.map((it, i) => (
              <li key={i} className="flex items-center justify-between px-3 py-2.5 text-sm">
                <div>
                  <div className="font-medium">{it.crop}</div>
                  <div className="text-xs text-muted-foreground">
                    {it.plants} plants × {it.est_yield_lbs} lbs @ ${usd(it.price_per_lb)}/lb
                  </div>
                </div>
                <div className="font-mono font-semibold text-green-700">${usd(it.est_value)}</div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <Source>{data?.pricing_source?.label} · yield assumption: {data?.pricing_source?.yield_assumption_lbs_per_plant || 5} lbs/plant · updated {fmtDate(data?.pricing_source?.fetched_at)}</Source>
    </div>
  );
}

// ---------- Livestock ----------
function LivestockDetail({ data }) {
  const items = data?.items || [];
  return (
    <div className="space-y-4 pt-2">
      <Total label="Total estimated value" amount={data?.total || 0} testid="modal-livestock-total" />
      {items.length === 0 ? (
        <div className="rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
          No active herds or flocks yet. Add them in the Livestock or Poultry tabs.
        </div>
      ) : (
        <div>
          <SectionLabel>Herd-by-herd breakdown</SectionLabel>
          <ul className="divide-y divide-muted rounded-lg border overflow-hidden">
            {items.map((it, i) => (
              <li key={i} className="flex items-center justify-between px-3 py-2.5 text-sm">
                <div>
                  <div className="font-medium">{it.name} <span className="text-xs text-muted-foreground uppercase">· {it.type}</span></div>
                  <div className="text-xs text-muted-foreground">
                    {it.count} head × {it.avg_weight_lbs || '~'} lbs @ ${usd(it.price_per_lb || 0)}/lb
                  </div>
                </div>
                <div className="font-mono font-semibold text-amber-700">${usd(it.est_value)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <Source>{data?.pricing_source?.label} · updated {fmtDate(data?.pricing_source?.fetched_at)}</Source>
    </div>
  );
}

// ---------- Dairy ----------
function DairyDetail({ data }) {
  const items = data?.items || [];
  const projected = data?.total || 0;
  const actual = data?.actual_monthly || 0;
  const actualUnits = data?.actual_units || 0;
  const history = data?.history_30d || [];
  const pct = comparisonPct(actual, projected);
  // Blended $/gal used for the actual figure (backend already applied per-animal-type prices,
  // but for the modal formula display we surface the weighted average the user sees).
  const priceRef = actualUnits > 0 ? (actual / actualUnits) : (items[0]?.price_per_gallon || 0);

  return (
    <div className="space-y-4 pt-2">
      <div className="grid grid-cols-2 gap-3">
        <Total label="Projected" amount={projected} colorClass="text-blue-800" testid="modal-dairy-projected" />
        <Total label="Actual" amount={actual} colorClass="text-emerald-700" sub={`${actualUnits} gal logged`} testid="modal-dairy-actual" />
      </div>

      {pct !== null && (
        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm" data-testid="modal-dairy-comparison">
          <TrendingUp className="w-4 h-4 text-emerald-700" />
          <span><b>{pct}%</b> of projected achieved so far this month</span>
        </div>
      )}

      <div>
        <SectionLabel>Projected formula</SectionLabel>
        {items.length === 0 ? (
          <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
            No dairy herds. Mark a goat/sheep/cattle herd&apos;s purpose as &ldquo;dairy&rdquo; to see a projection.
          </div>
        ) : (
          <ul className="divide-y divide-muted rounded-lg border overflow-hidden text-sm">
            {items.map((it, i) => (
              <li key={i} className="px-3 py-2.5">
                <div className="font-medium">{it.name} <span className="text-xs text-muted-foreground uppercase">· {it.type}</span></div>
                <div className="text-xs text-muted-foreground font-mono mt-1">
                  {it.head} × {it.daily_yield_per_head} gal/day × {it.days} days × ${usd(it.price_per_gallon)}/gal = <b className="text-blue-700">${usd(it.monthly_revenue)}</b>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <SectionLabel>Actual formula</SectionLabel>
        <div className="rounded-lg border px-3 py-2.5 text-sm">
          <span className="font-mono text-xs text-muted-foreground">
            {actualUnits} gal logged × ${usd(priceRef)}/gal = <b className="text-emerald-700">${usd(actual)}</b>
          </span>
        </div>
      </div>

      <HistoryList
        title="Last 30 days of logged milk"
        empty="No milk logged in the last 30 days."
        rows={history}
        rowKey={(r) => `${r.date}-${r.herd_id || r.foundation_animal_id}`}
        renderRow={(r) => (
          <>
            <span className="text-xs">{r.date}</span>
            <span className="text-xs text-muted-foreground">{r.animal_type}</span>
            <span className="font-mono text-xs font-semibold">{r.quantity} {r.unit || 'gal'}</span>
          </>
        )}
      />

      <Source>
        Pricing: {data?.pricing_source?.label}
        {data?.pricing_source?.price_keys_used?.length ? ` · slugs: ${data.pricing_source.price_keys_used.join(', ')}` : ''}
        {' · updated '}{fmtDate(data?.pricing_source?.fetched_at)}
      </Source>
    </div>
  );
}

// ---------- Eggs ----------
function EggsDetail({ data }) {
  const items = data?.items || [];
  const projected = data?.total || 0;
  const actual = data?.actual_monthly || 0;
  const actualUnits = data?.actual_units || 0;
  const history = data?.history_30d || [];
  const pct = comparisonPct(actual, projected);
  const pricePerDozen = data?.pricing_source?.price_per_dozen || items[0]?.price_per_dozen || 0;

  return (
    <div className="space-y-4 pt-2">
      <div className="grid grid-cols-2 gap-3">
        <Total label="Projected" amount={projected} colorClass="text-yellow-800" testid="modal-eggs-projected" />
        <Total label="Actual" amount={actual} colorClass="text-emerald-700" sub={`${int(actualUnits)} eggs logged`} testid="modal-eggs-actual" />
      </div>

      {pct !== null && (
        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm" data-testid="modal-eggs-comparison">
          <TrendingUp className="w-4 h-4 text-emerald-700" />
          <span><b>{pct}%</b> of projected achieved so far this month</span>
        </div>
      )}

      <div>
        <SectionLabel>Projected formula</SectionLabel>
        {items.length === 0 ? (
          <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
            No poultry flocks. Add a flock in the Poultry tab to see a projection.
          </div>
        ) : (
          <ul className="divide-y divide-muted rounded-lg border overflow-hidden text-sm">
            {items.map((it, i) => (
              <li key={i} className="px-3 py-2.5">
                <div className="font-medium">{it.name}</div>
                <div className="text-xs text-muted-foreground font-mono mt-1">
                  {it.birds} birds × {it.eggs_per_bird_per_day} eggs/day × {it.days} days ÷ 12 × ${usd(it.price_per_dozen)}/doz = <b className="text-yellow-700">${usd(it.monthly_revenue)}</b>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <SectionLabel>Actual formula</SectionLabel>
        <div className="rounded-lg border px-3 py-2.5 text-sm">
          <span className="font-mono text-xs text-muted-foreground">
            {int(actualUnits)} eggs ÷ 12 × ${usd(pricePerDozen)}/doz = <b className="text-emerald-700">${usd(actual)}</b>
          </span>
        </div>
      </div>

      <HistoryList
        title="Last 30 days of logged eggs"
        empty="No eggs logged in the last 30 days."
        rows={history}
        rowKey={(r) => `${r.date}-${r.flock_id}`}
        renderRow={(r) => (
          <>
            <span className="text-xs">{r.date}</span>
            <span className="text-xs text-muted-foreground">{r.entry_type || 'total'}</span>
            <span className="font-mono text-xs font-semibold">{int(r.count)} eggs</span>
          </>
        )}
      />

      <Source>Pricing: {data?.pricing_source?.label} · updated {fmtDate(data?.pricing_source?.fetched_at)}</Source>
    </div>
  );
}

// ---------- Small primitives ----------
function Total({ label, amount, colorClass = 'text-foreground', sub, testid }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`font-heading text-2xl font-bold tabular-nums ${colorClass}`} data-testid={testid}>
        ${usd(amount)}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{children}</div>;
}

function Source({ children }) {
  return (
    <p className="text-[10px] text-muted-foreground border-t pt-3 leading-relaxed">
      <span className="uppercase tracking-wide">Source · </span>{children}
    </p>
  );
}

function HistoryList({ title, empty, rows, rowKey, renderRow }) {
  return (
    <div>
      <SectionLabel>{title}</SectionLabel>
      {rows.length === 0 ? (
        <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">{empty}</div>
      ) : (
        <ul className="divide-y divide-muted rounded-lg border overflow-hidden max-h-56 overflow-y-auto">
          {rows.map((r) => (
            <li key={rowKey(r)} className="grid grid-cols-3 items-center px-3 py-2 gap-2">
              {renderRow(r)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
